// React
import React, { useEffect, useState, useRef } from 'react';

// Packages
import cc from 'classcat';
import t from 'prop-types';
import { useController } from 'react-hook-form';
import { nanoid } from 'nanoid';
import {
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
} from '@headlessui/react';

// Utilities
import { useLabels } from 'utilities/hooks';

// Components
import Button from 'components/button';
import EmptyState from '../emptyState';

// Icons
import { FiX, FiChevronDown, FiPlus, FiCheck } from 'react-icons/fi';

const TagsListComponent = ({
    controller,
    defaultValue,
    disabled,
    listMaxLength,
    missingOptionsLabel,
    name,
    object,
    options,
    placeholder,
    selectLabel,
    setValue,
    sort,
    theme,
}) => {
    // ///////////////////
    // OBJECT CONFIG
    // ///////////////////

    const { label: inputLabel, subLabel, errorLabel, required } = object || {};

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label } = useLabels();
    const {
        field: { onChange, value, ref },
        fieldState: { error },
    } = useController({
        name,
        control: controller,
        defaultValue: (defaultValue || []).filter(
            item => String(item).length > 0
        ),
        rules: {
            validate: {
                required: v => (required ? v?.length > 0 : true),
            },
        },
    });

    const {
        field: { onChange: onChangeDeleted },
    } = useController({
        name: `${name}_Removed`,
        control: controller,
        defaultValue: [],
    });

    // ///////////////////
    // STATE
    // ///////////////////

    const [asyncOptionsFetched, setAsyncOptionsFetched] = useState(false);
    const [loadedOptions, setLoadedOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [allowAutoOpen, setAllowAutoOpen] = useState(false);
    const [list, setList] = useState([{ value: '', id: nanoid() }]);
    const [deletedList, setDeletedList] = useState([]);

    // ///////////////////
    // METHODS
    // ///////////////////

    function onSelectChange(nextValue, item) {
        // Get next list
        const nextList = list.map(listItem =>
            listItem.id === item.id
                ? {
                      ...listItem,
                      value: nextValue,
                  }
                : listItem
        );

        // Update current list
        setList(nextList);

        if (item.value) {
            // Update removed value
            setDeletedList([...deletedList, item.value]);
        }

        onChange(
            nextList.filter(x => String(x.value).length > 0).map(x => x.value)
        );
    }

    function getPlaceholder() {
        if (loadingOptions) return label('FormCaptureSelectLoadingOptions');
        if (loadedOptions.length > 0)
            return placeholder || label('FormCaptureSelectEmpty');
        return label('FormCaptureSelectNoOptions');
    }

    // ///////////////////
    // DATA
    // ///////////////////

    const columnWidth = {
        'col-span-full': (value?.length || 0) === 0,
        'col-span-11': (value?.length || 0) > 0,
    };

    const missingOptions = missingOptionsLabel && loadedOptions.length === 0;

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        // Assume normal options
        if (Array.isArray(options)) {
            setLoadedOptions(options);
            return;
        }

        // Or perhaps async options
        async function getOptions() {
            setLoadingOptions(true);
            setLoadedOptions(await options());
            setLoadingOptions(false);
            setAsyncOptionsFetched(true);
        }

        if (!asyncOptionsFetched) getOptions();
    }, [options]);

    // Set value from beginning
    useEffect(() => {
        if ((defaultValue || []).length > 0) {
            setList(
                defaultValue.map(item => ({
                    value: item,
                    id: nanoid(),
                }))
            );
            setValue(name, defaultValue);
        }
    }, [loadedOptions]);

    // Update local state when using setValue
    useEffect(() => {
        if (value && value.length > 0) {
            setList(
                value.map(item => ({
                    value: item,
                    id: nanoid(),
                }))
            );
        }
    }, [value, loadedOptions]);

    // Form onChangeDeleted
    useEffect(() => {
        if (value && value.length > 0) {
            onChangeDeleted(deletedList.filter(x => !value.includes(x)));
        } else {
            onChangeDeleted(deletedList);
        }
    }, [value]);

    // ///////////////////
    // THEMING
    // ///////////////////

    const isBlue = theme === 'blue';
    const isBlueAlt = theme === 'blue-alt';
    const isTeal = theme === 'teal';

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <label className="flex flex-col" htmlFor={name} ref={ref}>
            {inputLabel && (
                <span
                    className={cc([
                        'input-label t-h5',
                        {
                            'input-label-blue': isBlue,
                            'input-label-blue-alt': isBlueAlt,
                            'input-label-teal': isTeal,
                        },
                    ])}>
                    {inputLabel}
                    {required && <span>({label('FormCaptureRequired')})</span>}
                </span>
            )}

            {subLabel && (
                <span
                    className={cc([
                        'mt-8 input-sublabel',
                        {
                            'input-sublabel-blue': isBlue,
                            'input-sublabel-blue-alt': isBlueAlt,
                            'input-sublabel-teal': isTeal,
                        },
                    ])}>
                    {subLabel}
                </span>
            )}

            <div
                className={cc([
                    'flex flex-col',
                    { 'mt-16': inputLabel || subLabel },
                ])}>
                {missingOptions && (
                    <EmptyState theme={theme} label={missingOptionsLabel} />
                )}

                {!missingOptions && (
                    <>
                        {selectLabel && (
                            <div
                                className={cc([
                                    'grid grid-cols-12 gap-12 mb-4 input-utility-text',
                                    {
                                        'input-utility-text-blue': isBlue,
                                        'input-utility-text-teal': isTeal,
                                    },
                                ])}>
                                <span className={cc([columnWidth])}>
                                    {selectLabel || ''}
                                </span>
                            </div>
                        )}

                        <div className="flex flex-col space-y-12">
                            {list.map(item => (
                                <ListRow
                                    key={item.id}
                                    {...{
                                        columnWidth,
                                        disabled,
                                        error,
                                        getPlaceholder,
                                        isBlue,
                                        isBlueAlt,
                                        isTeal,
                                        item,
                                        list,
                                        deletedList,
                                        loadedOptions,
                                        name,
                                        onSelectChange,
                                        sort,
                                        setList,
                                        setDeletedList,
                                        setLoadedOptions,
                                        theme,
                                        value,
                                        allowAutoOpen,
                                        onChange,
                                        setAllowAutoOpen,
                                    }}
                                />
                            ))}
                        </div>

                        {error && (
                            <div className="flex mt-6 input-utility-text">
                                <div className="input-utility-text-error">
                                    {errorLabel}
                                </div>
                            </div>
                        )}

                        <AddButton
                            {...{
                                list,
                                listMaxLength,
                                options: loadedOptions,
                                setList,
                                value,
                                theme,
                                setAllowAutoOpen,
                            }}
                        />
                    </>
                )}
            </div>
        </label>
    );
};

const ListRow = ({
    columnWidth,
    disabled,
    error,
    getPlaceholder,
    isBlue,
    isBlueAlt,
    isTeal,
    item,
    list,
    deletedList,
    loadedOptions,
    onSelectChange,
    setList,
    setLoadedOptions,
    setDeletedList,
    sort,
    theme,
    value,
    allowAutoOpen,
    onChange,
    setAllowAutoOpen,
}) => {
    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label } = useLabels();
    const inputRef = useRef(null);

    // ///////////////////
    // STATE
    // ///////////////////

    const [query, setQuery] = useState('');

    // ///////////////////
    // DATA
    // ///////////////////

    const realOptions = loadedOptions
        .sort((a, b) => (sort ? a?.label?.localeCompare(b?.label) : 0))
        .filter(option => !option.disabledInSelect)
        .filter(option =>
            query === ''
                ? true
                : option.label
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

    const realValue = item.value
        ? loadedOptions.find(option => option.value === item.value)
        : null;

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        if (allowAutoOpen) {
            if (inputRef?.current && String(item.value).length === 0) {
                inputRef.current.focus();
                setTimeout(() => {
                    inputRef.current?.focus();
                    inputRef.current?.select?.();
                }, 100);
            }
            // prevent repeated auto-open focusing for subsequent renders
            setAllowAutoOpen(false);
        }
    }, [allowAutoOpen]);

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <div>
            <div className="grid grid-cols-12 gap-12">
                <div
                    className={cc(['relative flex items-center', columnWidth])}>
                    <Combobox
                        value={realValue}
                        immediate
                        onChange={opt => {
                            onSelectChange(opt.value, item);
                            setQuery('');
                        }}>
                        {({ open }) => (
                            <>
                                {disabled || loadedOptions.length === 0 ? (
                                    <input
                                        className={cc([
                                            'input-defaults',
                                            'appearance-none grow !pr-[32px] max-w-full',
                                            {
                                                'input-defaults-blue': isBlue,
                                                'input-defaults-blue-alt': isBlueAlt,
                                                'input-defaults-teal': isTeal,
                                                'input-defaults-error': error,
                                            },
                                        ])}
                                        placeholder={getPlaceholder()}
                                        disabled
                                    />
                                ) : (
                                    <ComboboxInput
                                        ref={inputRef}
                                        className={cc([
                                            'input-defaults',
                                            'appearance-none grow !pr-[32px] max-w-full',
                                            {
                                                'input-defaults-blue': isBlue,
                                                'input-defaults-blue-alt': isBlueAlt,
                                                'input-defaults-teal': isTeal,
                                                'input-defaults-error': error,
                                            },
                                        ])}
                                        displayValue={x => (x ? x.label : '')}
                                        placeholder={getPlaceholder()}
                                        onChange={event =>
                                            setQuery(event.target.value)
                                        }
                                    />
                                )}

                                {loadedOptions.length > 0 && (
                                    <>
                                        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center justify-center w-32">
                                            <FiChevronDown className="stroke-current" />
                                        </ComboboxButton>

                                        {open && (
                                            <ComboboxOptions
                                                onMouseDown={e =>
                                                    e.preventDefault()
                                                }
                                                style={{
                                                    top: 'calc(100% + 5px)',
                                                }}
                                                className={cc([
                                                    'input-defaults-select-options',
                                                    {
                                                        'input-defaults-select-options-blue': isBlue,
                                                        'input-defaults-select-options-blue-alt': isBlueAlt,
                                                        'input-defaults-select-options-teal': isTeal,
                                                    },
                                                ])}>
                                                {realOptions.length > 0 ? (
                                                    realOptions.map(
                                                        (option, index) => {
                                                            const disabledOption = list
                                                                .map(
                                                                    x => x.value
                                                                )
                                                                .includes(
                                                                    option.value
                                                                );

                                                            return (
                                                                <ComboboxOption
                                                                    key={`${option.value}-${index}`}
                                                                    value={
                                                                        option
                                                                    }
                                                                    disabled={
                                                                        disabledOption
                                                                    }
                                                                    className={({
                                                                        focus,
                                                                        selected,
                                                                    }) =>
                                                                        cc([
                                                                            'input-defaults-select-option',
                                                                            {
                                                                                'opacity-70': disabledOption,
                                                                                'input-defaults-select-option-blue': isBlue,
                                                                                'input-defaults-select-option-blue-alt': isBlueAlt,
                                                                                'input-defaults-select-option-teal': isTeal,
                                                                                'input-defaults-select-option-active-blue':
                                                                                    focus &&
                                                                                    isBlue,
                                                                                'input-defaults-select-option-active-blue-alt':
                                                                                    focus &&
                                                                                    isBlueAlt,
                                                                                'input-defaults-select-option-active-teal':
                                                                                    focus &&
                                                                                    isTeal,
                                                                                'input-defaults-select-option-selected': selected,
                                                                            },
                                                                        ])
                                                                    }>
                                                                    {({
                                                                        selected,
                                                                    }) => (
                                                                        <div className="relative flex items-center">
                                                                            {selected && (
                                                                                <FiCheck className="absolute" />
                                                                            )}
                                                                            <span className="ml-24">
                                                                                {
                                                                                    option.label
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </ComboboxOption>
                                                            );
                                                        }
                                                    )
                                                ) : (
                                                    <div
                                                        className={cc([
                                                            'input-defaults-select-option',
                                                            {
                                                                'input-defaults-select-option-blue': isBlue,
                                                                'input-defaults-select-option-blue-alt': isBlueAlt,
                                                                'input-defaults-select-option-teal': isTeal,
                                                            },
                                                        ])}>
                                                        {label(
                                                            'FormCaptureSelectNothingFound'
                                                        )}
                                                    </div>
                                                )}
                                            </ComboboxOptions>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Combobox>
                </div>

                <DeleteButton
                    {...{
                        item,
                        list,
                        deletedList,
                        setList,
                        setDeletedList,
                        value,
                        loadedOptions,
                        setLoadedOptions,
                        theme,
                        onChange,
                    }}
                />
            </div>
        </div>
    );
};

const DeleteButton = ({
    item,
    list,
    deletedList,
    setList,
    setDeletedList,
    value,
    loadedOptions,
    setLoadedOptions,
    theme,
    onChange,
}) => {
    function deleteAction() {
        setDeletedList([...deletedList, item.value]);

        if (value.length === 1 && list.length === 1) {
            setList([{ value: '', id: nanoid() }]);
            onChange([]);
        } else if (value.length > 0 && list.length === value.length + 1) {
            setList(list.filter(l => l.value));
        } else if (value.length > 0 && list.length === value.length) {
            const nextList = [...list.filter(l => l.id !== item.id)];
            setList(nextList);
            onChange(
                nextList
                    .filter(x => String(x.value).length > 0)
                    .map(x => x.value)
            );
        }

        // Preserve original behavior
        const firstEnabled = loadedOptions.find(x => x.value);
        if (firstEnabled) firstEnabled.optionDisabled = false;

        setLoadedOptions(loadedOptions);
    }

    return (
        value?.length > 0 && (
            <div className="flex justify-end col-span-1">
                <Button
                    variant="tertiary"
                    theme={theme}
                    icon={FiX}
                    disabled={item.disabled}
                    className="self-end !px-8"
                    iconPosition="center"
                    action={deleteAction}
                />
            </div>
        )
    );
};

const AddButton = ({
    list,
    listMaxLength,
    options,
    setList,
    value,
    theme,
    setAllowAutoOpen,
}) =>
    [
        listMaxLength > 1,
        options.length > 1,
        list.length < options.length,
        list.length === value?.length,
        list.length < listMaxLength,
    ].every(x => x) && (
        <Button
            variant="secondary"
            theme={theme}
            className="self-start mt-12"
            icon={FiPlus}
            iconPosition="center"
            action={() => {
                setAllowAutoOpen(true);
                setList([...list, { value: '', id: nanoid() }]);
            }}
        />
    );

TagsListComponent.propTypes = {
    controller: t.object.isRequired,
    defaultValue: t.arrayOf(t.oneOfType([t.string, t.number])),
    disabled: t.bool,
    listMaxLength: t.number,
    missingOptionsLabel: t.string,
    name: t.string.isRequired,
    object: t.object.isRequired,
    options: t.oneOfType([
        t.func,
        t.arrayOf(
            t.shape({
                label: t.string,
                value: t.oneOfType([t.string, t.number, t.bool]),
            })
        ),
    ]),
    placeholder: t.string,
    selectLabel: t.string,
    setValue: t.func,
    sort: t.bool,
    theme: t.oneOf(['teal', 'blue', 'blue-alt']),
};

TagsListComponent.defaultProps = {
    options: [],
    defaultValue: [],
    showText: false,
    listMaxLength: 5,
    theme: 'teal',
    setValue() {},
    sort: true,
};

export default TagsListComponent;
